import type { FluxErrorEntityErrorCodes } from '../types.js';

export const RoleErrorCodes = {
  RoleAuthUserNoRole: {
    code: 'role/auth-user-no-role',
    message: 'O usuário autenticado não possui um cargo definido.',
    explanation: 'O usuário autenticado não possui um papel (role) definido.',
    localizations: {},
  },
  RoleContextConflict: {
    code: 'role/context-conflict',
    message: 'O cargo informado possui um conflito de contexto.',
    explanation:
      'O papel (role) fornecido tem um conflito de contexto. Certifique-se de que o papel seja apropriado para o escopo (conta, banco de dados, fluxo, etc.) em que está sendo atribuído. Verifique se o id da entidade relacionada está correto e que nenhum id conflitante está sendo usado.',
    localizations: {},
  },
  RoleDoesNotBelongToDatabase: {
    code: 'role/does-not-belong-to-database',
    message: 'O cargo informado não pertence ao banco de dados especificado.',
    explanation: 'O papel (role) fornecido não pertence ao banco de dados especificado.',
    localizations: {},
  },
  RoleGuestToPipe: {
    code: 'role/guest-to-pipe',
    message: 'Um convidado não pode ser atribuído a um fluxo com permissões acima de leitor.',
    explanation: 'Um convidado não pode ser atribuído a um fluxo (pipe) com permissões superiores a "leitor".',
    localizations: {},
  },
  RoleInsufficientPermissions: {
    code: 'role/insufficient-permissions',
    message: 'Você não tem permissão para esta ação.',
    explanation: 'Você não tem permissão necessária para atribuir a este papel (role). Para atribuir uma permissão a um papel, você deve essa permissão.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Papéis',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  RoleInvalidBulkUsers: {
    code: 'role/invalid-bulk-users',
    message: 'O número de usuários informados é inválido. Verifique suas informações e tente novamente.',
    explanation: 'O número de usuários fornecido para a operação em massa de papéis (roles) é inválido. Deve ser uma lista válida de IDs de usuários com no mínimo um usuário.',
    localizations: {},
  },
  RoleInvalidType: {
    code: 'role/invalid-type',
    message: 'O tipo de cargo informado não é válido.',
    explanation: 'O tipo de papel (role) fornecido é inválido. Verifique se o tipo está correto e existe para esse escopo.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Papéis',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  RoleIsManaged: {
    code: 'role/is-managed',
    message: 'Este cargo é gerenciado pelo sistema e não pode ser alterado.',
    explanation: 'Este papel (role) é gerenciado pelo sistema e não pode ser alterado. Alguns papéis padrão são protegidos.',
    localizations: {},
  },
  RoleLicenseRequired: {
    code: 'role/license-required',
    message: 'É necessária uma licença ativa para atribuir este cargo ao usuário.',
    explanation: 'Uma licença ativa é necessária para atribuir este papel (role) a um usuário. Alterações de papéis podem exigir licenças específicas dependendo das permissões envolvidas.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Licenças',
        path: '/wiki/docs/licencas/intro',
      },
      {
        name: 'Operador',
        path: '/wiki/docs/licencas/operador',
      },
    ],
  },
  RoleManageUserAboveHierarchy: {
    code: 'role/manage-user-above-hierarchy',
    message: 'Você não pode gerenciar um usuário com um cargo igual ou superior ao seu.',
    explanation: 'Você não pode gerenciar um usuário com um papel (role) igual ou superior ao seu na hierarquia.',
    localizations: {},
  },
  RoleNotFound: {
    code: 'role/not-found',
    message: 'Cargo não encontrado.',
    explanation: 'O papel (role) que você está tentando acessar não foi encontrado.',
    localizations: {},
  },
  RoleOwnerRole: {
    code: 'role/owner-role',
    message: 'Não é possível definir um usuário como proprietário.',
    explanation: 'Você não pode definir um usuário como proprietário. O papel de proprietário é exclusivo.',
    localizations: {},
  },
  RoleSetAboveHierarchy: {
    code: 'role/set-above-hierarchy',
    message: 'Você não pode atribuir um cargo igual ou superior ao seu na hierarquia.',
    explanation: 'Você não pode atribuir um papel (role) igual ou superior ao seu na hierarquia. Apenas usuários com cargos superiores podem atribuir este nível de permissão. ',
    localizations: {},
    supportingDocs: [
      {
        name: 'Papéis',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  RoleUserAlreadyInRole: {
    code: 'role/user-already-in-role',
    message: 'O usuário já possui este cargo.',
    explanation: 'O usuário já possui este papel (role).',
    localizations: {},
  },
  RoleUserNoRole: {
    code: 'role/user-no-role',
    message: 'O usuário não possui um cargo atribuído.',
    explanation: 'O usuário não possui um papel (role) atribuído.',
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'role'>;
